import type { AuthPort, AuthState } from "./ports.ts";

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

const environment = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env ?? {};

const clientHeaders = {
  "X-Orni-Client": "web",
  "X-Orni-Client-Version": environment.VITE_ORNI_APP_VERSION?.trim() || "0.1.0",
  "X-Orni-Protocol-Version": "1",
};

export interface BrowserAuthOptions {
  fetch?: Fetcher;
  getReturnTo?: () => string;
  navigate?: (url: string) => void;
}

const returnToBase = "https://orni.invalid";

export function safeRelativeReturnTo(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  try {
    const parsed = new URL(value, returnToBase);
    if (parsed.origin !== returnToBase) return "/";
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return "/";
  }
}

export class BrowserAuth implements AuthPort {
  private currentState: AuthState = "checking";
  private currentReason: string | null = null;
  private readonly listeners = new Set<(state: AuthState) => void>();
  private readonly fetcher: Fetcher;
  private readonly getReturnTo: () => string;
  private readonly navigate: (url: string) => void;
  private initialization: Promise<void> | null = null;
  private refreshRequest: Promise<boolean> | null = null;
  // Login can finish while the first /auth/session request is still in
  // flight. Keep that stale response from moving a newly authenticated
  // browser back to the signed-out screen.
  private authenticatedOverride = false;

  constructor(options: BrowserAuthOptions = {}) {
    this.fetcher = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.getReturnTo = options.getReturnTo ?? (() => (
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    ));
    this.navigate = options.navigate ?? ((url) => window.location.assign(url));
  }

  get state(): AuthState {
    return this.currentState;
  }

  get reason(): string | null {
    return this.currentReason;
  }

  initialize(): Promise<void> {
    this.initialization ??= this.loadSession();
    return this.initialization;
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    listener(this.currentState);
    return () => this.listeners.delete(listener);
  }

  async getAccessToken(): Promise<null> {
    return null;
  }

  recoverUnauthorized(): Promise<boolean> {
    this.refreshRequest ??= this.refreshWithBrowserLock().finally(() => {
      this.refreshRequest = null;
    });
    return this.refreshRequest;
  }

  /**
   * Refresh cookies under the browser's cross-tab Web Lock. Refresh tokens
   * rotate on every successful refresh, so two tabs using the same old cookie
   * at once would otherwise make the second tab appear signed out.
   */
  private refreshWithBrowserLock(): Promise<boolean> {
    const locks = typeof navigator !== "undefined"
      ? (navigator as unknown as {
          locks?: { request: (name: string, callback: () => Promise<boolean>) => Promise<boolean> };
        }).locks
      : undefined;
    if (!locks) return this.refreshCookies();
    return locks.request("orni-cloud-auth-refresh", () => this.refreshCookies());
  }

  async signIn(): Promise<void> {
    const returnTo = safeRelativeReturnTo(this.getReturnTo());
    this.navigate(`/login?return_to=${encodeURIComponent(returnTo)}`);
  }

  async signOut(): Promise<void> {
    const response = await this.fetcher("/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", ...clientHeaders },
    });
    if (!response.ok) throw new Error(`Sign out failed with HTTP ${response.status}`);
    this.authenticatedOverride = false;
    this.update("signed_out", null);
  }

  /** Mark the browser session as authenticated after a same-origin login. */
  markAuthenticated(): void {
    this.authenticatedOverride = true;
    this.update("authenticated", null);
  }

  private async loadSession(): Promise<void> {
    try {
      const response = await this.fetcher("/auth/session", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      headers: { Accept: "application/json", ...clientHeaders },
      });
      if (response.status === 401) {
        if (this.authenticatedOverride) return;
        await this.recoverUnauthorized();
        return;
      }
      if (!response.ok) {
        this.update("unavailable", `Session check failed with HTTP ${response.status}.`);
        return;
      }

      const payload: unknown = await response.json();
      if (!payload || typeof payload !== "object" || !("authenticated" in payload)) {
        this.update("unavailable", "Session response is invalid.");
        return;
      }
      const authenticated = (payload as { authenticated?: unknown }).authenticated;
      if (typeof authenticated === "boolean") {
        if (authenticated) {
          this.authenticatedOverride = true;
          this.update("authenticated", null);
        } else {
          if (this.authenticatedOverride) return;
          await this.recoverUnauthorized();
        }
        return;
      }
      this.update("unavailable", "Session response is invalid.");
    } catch {
      this.update("unavailable", "Cloud authentication could not be reached.");
    }
  }

  private async refreshCookies(): Promise<boolean> {
    try {
      const response = await this.fetcher("/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", ...clientHeaders },
      });
      if (response.ok) {
        this.authenticatedOverride = true;
        this.update("authenticated", null);
        return true;
      }
      if (response.status === 401 || response.status === 403) {
        // An API request can discover an expired/revoked browser session long
        // after the initial /auth/session check. Do not keep the shell marked
        // authenticated while every Cloud request is rejected; that state was
        // previously rendered as an empty device/session list.
        this.authenticatedOverride = false;
        this.update("signed_out", null);
        return false;
      }
      this.update("unavailable", `Session refresh failed with HTTP ${response.status}.`);
      return false;
    } catch {
      this.update("unavailable", "Cloud authentication could not be reached.");
      return false;
    }
  }

  private update(state: AuthState, reason: string | null): void {
    if (this.currentState === state && this.currentReason === reason) return;
    this.currentState = state;
    this.currentReason = reason;
    for (const listener of this.listeners) listener(state);
  }
}
